import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useCompaniesStore } from "../../../../../../store/admin/companiesStore";
import { TONE_CLASS, ago, fetchUnitHistory, typeMeta, notificationTitle } from "../../../../../../features/unitNotifications/unitNotifications";
import { useEscapeKey } from "../../../../../../hooks/useEscapeKey";

interface Props {
  vehicleId: string;
  unitName: string;
  isDarkMode: boolean;
  onClose: () => void;
}

const DAYS = 7;

/**
 * «Notificaciones» de una unidad desde su tarjeta (la pestaña de Mapon): lo de
 * los últimos 7 días y un atajo a Alertas ya filtrada por la unidad.
 */
export default function UnitNotificationsModal({ vehicleId, unitName, isDarkMode, onClose }: Props) {
  const companyId = useCompaniesStore((s) => s.companyId);
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["unitNotifications", "unit", companyId, vehicleId],
    queryFn: () => {
      const to = new Date();
      return fetchUnitHistory(companyId!, { from: new Date(to.getTime() - DAYS * 86_400_000).toISOString(), to: to.toISOString(), vehicleId });
    },
    enabled: !!companyId && !!vehicleId,
    staleTime: 60_000,
  });

  useEscapeKey(onClose);

  const panel = isDarkMode ? "bg-dark-card border-dark-border text-white" : "bg-white border-gray-200 text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 sm:p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="unit-notif-title"
        onClick={(e) => e.stopPropagation()}
        className={`w-full sm:max-w-md max-h-[85vh] flex flex-col border rounded-t-2xl sm:rounded-2xl shadow-2xl ${panel}`}
      >
        <div className={`flex items-start justify-between gap-3 px-4 py-3 border-b ${isDarkMode ? "border-dark-border" : "border-gray-200"}`}>
          <div className="min-w-0">
            <h2 id="unit-notif-title" className="text-sm font-semibold">Notificaciones</h2>
            <p className={`text-xs truncate ${muted}`}>{unitName} · últimos {DAYS} días</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className={`text-xl ${muted}`}>
            <i className="ri-close-line" />
          </button>
        </div>
        <ul className="flex-1 overflow-y-auto">
          {isLoading && <li className={`px-4 py-6 text-sm text-center ${muted}`}>Cargando…</li>}
          {isError && <li className={`px-4 py-6 text-sm text-center ${muted}`}>No se pudieron cargar las notificaciones.</li>}
          {data && data.items.length === 0 && (
            <li className={`px-4 py-8 text-sm text-center ${muted}`}>Sin notificaciones de esta unidad en los últimos {DAYS} días.</li>
          )}
          {data?.items.map((n) => {
            const meta = typeMeta(n.type);
            return (
              <li key={n.id} className="flex gap-3 px-4 py-3">
                <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${TONE_CLASS[meta.tone]}`}>
                  <i className={`${meta.icon} text-base`} aria-hidden="true"></i>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="text-sm truncate">{notificationTitle(n)}</span>
                    <span className={`text-[11px] shrink-0 tabular-nums ${muted}`}>{ago(n.created_at)}</span>
                  </span>
                  <span className={`block text-xs mt-0.5 line-clamp-2 ${muted}`}>{n.description}</span>
                </span>
              </li>
            );
          })}
        </ul>
        <div className={`px-4 py-3 border-t ${isDarkMode ? "border-dark-border" : "border-gray-200"}`}>
          <button type="button" onClick={() => navigate(`/reports/alerts?vehicle=${encodeURIComponent(vehicleId)}`)} className="text-sm text-primary-500 hover:underline">
            Ver todas en Alertas →
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
